import { Component, Prop, h } from '@stencil/core';
import { parseJsonProp } from '../../../../../utils/helpers/hydration';

const COMPONENT_NAME = 'sn-cases';

/**
 * Case-examples section presenting two real-world scenarios as side-by-side cards.
 * Centered heading + subtitle above a 2-column card row (6/6 on desktop, stacked on mobile); first card uses a grey background, second card uses a blue background, each with title, intro text, bullet list, and conclusion.
 * Only type in the `cases` folder; capped at exactly 2 cards by design to highlight a paired before/after style comparison.
 *
 * @category SignNow
 * @slot heading - {content} <me-heading variant="4" tag=["h1", "h2", "h3", "h4", "h5", "h6", "div"]> - Section heading for real-world use cases of {keyword}. 6-10 words.
 * @slot subtitle - {content} <me-text variant="large"> - Introductory paragraph for the case cards. You MUST write 15-30 words.
 * @example
 *   <sn-cases
 *     cases='[{"title":"Real estate","text":"A regional agency.","items":["4-hour turnaround"],"conclusion":"More deals closed."},{"title":"HR","text":"Enterprise HR team.","items":["60 hours saved"],"conclusion":"Cost reduced 70%."}]'
 *   >
 *     <me-heading slot="heading" variant="4" tag="h2">Real-World Examples</me-heading>
 *     <me-text slot="subtitle" variant="large">See how businesses use eSignatures.</me-text>
 *   </sn-cases>
 */
@Component({
  tag: 'sn-cases',
  styleUrl: 'index.scss',
  shadow: false,
})
export class DpcCaseExamples {
  @Prop() colorMode: 'signnow' | 'inverted' = 'signnow';
  /**
   * Case example cards (max 2 — index 0 = grey, index 1 = blue).
   * @content
   * @type {array}
   * @item.type {object}
   * @item.property {string} title - Industry or role label for this case. 2-5 words.
   * @item.property {string} text - Description of the challenge and how {keyword} resolved it. You MUST write 15-30 words.
   * @item.property {array} items - Bullet list items (strings)
   * @item.property {string} conclusion - Outcome or result of using {keyword} in this case. You MUST write 30-50 words.
   * @item.required title
   * @item.required text
   * @item.content title text items conclusion
   * @minItems 2
   * @maxItems 2
   */
  @Prop({ mutable: true }) cases?: { title?: string; text?: string; items?: string[]; conclusion?: string }[] | string;

  /**
   * Legacy / raw-HTML fallback. For new usage prefer slots + `cases` prop.
   * @dynamic
   * @type {object}
   * @property {string} raw_html - Raw HTML content (replaces entire block)
   * @property {string} title - Fallback heading (use slot="heading" instead)
   * @property {string} subtitle - Fallback subtitle (use slot="subtitle" instead)
   * @property {array} cases - Fallback card data (use cases prop instead)
   */
  @Prop({ mutable: true }) values?:
    | {
        title?: string;
        subtitle?: string;
        raw_html?: string;
        cases?: { title?: string; text?: string; items?: string[]; conclusion?: string }[];
      }
    | string;

  componentWillLoad() {
    this.values = parseJsonProp(this.values);
    this.cases  = parseJsonProp(this.cases);
  }

  render() {
    const values = typeof this.values === 'object' ? this.values : undefined;

    if (values?.raw_html) {
      return <me-section class={COMPONENT_NAME} yGap="md" yGapLg="xl" innerHTML={values.raw_html}></me-section>;
    }

    const cases = Array.isArray(this.cases)
      ? this.cases
      : (Array.isArray(values?.cases) ? values.cases : []);

    return (
      <me-section class={COMPONENT_NAME} yGap="3xl" yGapLg="5xl">
        <div class={`${COMPONENT_NAME}__header`}>
          <slot name="heading"></slot>
          <slot name="subtitle"></slot>
        </div>
        {cases.length > 0 && (
          <div class={`${COMPONENT_NAME}__cards`}>
            {cases.map((caseItem, index) => {
              const caseItems = Array.isArray(caseItem.items) ? caseItem.items : [];
              return (
                <div class={`${COMPONENT_NAME}__card ${index === 1 ? `${COMPONENT_NAME}__card--blue` : ''}`}>
                  {caseItem.title && <me-heading variant="5" tag="h3" class={`${COMPONENT_NAME}__card-title`}>{caseItem.title}</me-heading>}
                  <div class={`${COMPONENT_NAME}__card-body`}>
                    {caseItem.text && <me-text class={`${COMPONENT_NAME}__card-text`}>{caseItem.text}</me-text>}
                    {caseItems.length > 0 && (
                      <ul class={`${COMPONENT_NAME}__list`}>
                        {caseItems.map((item) => (
                          <li class={`${COMPONENT_NAME}__list-item`}>{item}</li>
                        ))}
                      </ul>
                    )}
                    {caseItem.conclusion && (
                      <me-text class={`${COMPONENT_NAME}__conclusion`}>
                        {caseItem.conclusion}
                      </me-text>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </me-section>
    );
  }
}
